import React, { useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Modal,
  Space,
  Table,
  Tabs,
  Upload,
  message,
  Spin,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';

import shareTransferReconcileService from '../../../../services/shareTransferReconcileService';
import {
  buildBulkTransferPayload,
  getAlreadyTransferredColumns,
  getBlockedMatchColumns,
  getReadyMatchColumns,
  getUnmatchedExtractionColumns,
  getUnmatchedOrderColumns,
  hasDuplicateFingerprintOverlap,
  rowKeyForMatch,
} from './shareTransferMatch.helpers';

const { Dragger } = Upload;

function ShareTransferReconcileDrawer({ open, onClose, onSuccess }) {
  const [step, setStep] = useState('upload');
  const [extracting, setExtracting] = useState(false);
  const [matching, setMatching] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reconcileData, setReconcileData] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const resetState = () => {
    setStep('upload');
    setExtracting(false);
    setMatching(false);
    setConfirming(false);
    setReconcileData(null);
    setSelectedRowKeys([]);
    setConfirmOpen(false);
    setBulkResult(null);
  };

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  const runMatch = async (extractions) => {
    setMatching(true);
    try {
      const data = await shareTransferReconcileService.matchShareTransfers(extractions);
      setReconcileData(data);
      const readyKeys = (data.matched || [])
        .filter((row) => row.match_state === 'ready')
        .map(rowKeyForMatch);
      setSelectedRowKeys(readyKeys);
      setStep('review');
    } catch (err) {
      message.error(
        err?.response?.data?.message || err?.message || 'Reconciliation failed'
      );
    } finally {
      setMatching(false);
    }
  };

  const handleUpload = async (file) => {
    setExtracting(true);
    try {
      const ocrResponse = await shareTransferReconcileService.extractShareTransferPdf(file);
      await runMatch(ocrResponse);
    } catch (err) {
      const raw = err?.response?.data;
      const errMsg =
        (typeof raw === 'object' && (raw?.message || raw?.error)) ||
        (typeof raw === 'string' ? raw : null) ||
        err?.message ||
        'OCR extraction failed';
      message.error(errMsg);
    } finally {
      setExtracting(false);
    }
    return false;
  };

  const readyRows = (reconcileData?.matched || []).filter((r) => r.match_state === 'ready');
  const blockedRows = (reconcileData?.matched || []).filter((r) => r.match_state === 'blocked');
  const unmatchedExtractions = reconcileData?.unmatched_extractions || [];
  const unmatchedOrders = reconcileData?.unmatched_orders || [];
  const ambiguousRows = reconcileData?.ambiguous || [];
  const alreadyTransferredRows = reconcileData?.already_transferred || [];

  const selectedReadyRows = readyRows.filter((row) =>
    selectedRowKeys.includes(rowKeyForMatch(row))
  );

  const runBulkTransfer = async () => {
    if (selectedReadyRows.length === 0) {
      message.warning('Select at least one ready row to transfer.');
      return;
    }

    setConfirming(true);
    try {
      const payload = buildBulkTransferPayload(selectedReadyRows);
      const result = await shareTransferReconcileService.bulkTransferShares(payload);
      setBulkResult(result);

      if (result.summary?.failed === 0) {
        message.success(`Marked ${result.summary.succeeded} order(s) as shares transferred.`);
        setConfirmOpen(false);
        onSuccess?.();
        handleClose();
      } else if (result.summary?.succeeded > 0) {
        message.warning(
          `${result.summary.succeeded} succeeded, ${result.summary.failed} failed.`
        );
        setConfirmOpen(false);
        onSuccess?.();
      } else {
        message.error('Bulk transfer failed for all selected orders.');
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || err?.message || 'Bulk transfer failed'
      );
    } finally {
      setConfirming(false);
    }
  };

  const tabItems = [
    {
      key: 'ready',
      label: `Ready (${readyRows.length})`,
      children: (
        <Table
          rowKey={rowKeyForMatch}
          size="small"
          dataSource={readyRows}
          columns={getReadyMatchColumns()}
          pagination={false}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            getCheckboxProps: () => ({ disabled: false }),
          }}
          locale={{ emptyText: 'No ready matches' }}
        />
      ),
    },
    {
      key: 'blocked',
      label: `Blocked (${blockedRows.length})`,
      children: (
        <Table
          rowKey={rowKeyForMatch}
          size="small"
          dataSource={blockedRows}
          columns={getBlockedMatchColumns()}
          pagination={false}
          locale={{ emptyText: 'No blocked matches' }}
        />
      ),
    },
    {
      key: 'already_transferred',
      label: `Already transferred (${alreadyTransferredRows.length})`,
      children: (
        <Table
          rowKey={rowKeyForMatch}
          size="small"
          dataSource={alreadyTransferredRows}
          columns={getAlreadyTransferredColumns()}
          pagination={false}
          locale={{ emptyText: 'No already-transferred matches' }}
        />
      ),
    },
    {
      key: 'unmatched_pdf',
      label: `Unmatched PDF (${unmatchedExtractions.length})`,
      children: (
        <Table
          rowKey={(row) => `ext-${row.extraction?.row_index}`}
          size="small"
          dataSource={unmatchedExtractions}
          columns={getUnmatchedExtractionColumns()}
          pagination={false}
          locale={{ emptyText: 'All PDF rows matched' }}
        />
      ),
    },
    {
      key: 'unmatched_orders',
      label: `Unmatched orders (${unmatchedOrders.length})`,
      children: (
        <Table
          rowKey="uuid"
          size="small"
          dataSource={unmatchedOrders}
          columns={getUnmatchedOrderColumns()}
          pagination={false}
          locale={{ emptyText: 'All payment-received orders matched' }}
        />
      ),
    },
  ];

  if (ambiguousRows.length > 0) {
    tabItems.splice(2, 0, {
      key: 'ambiguous',
      label: `Ambiguous (${ambiguousRows.length})`,
      children: (
        <>
          <Alert
            type="warning"
            showIcon
            className="mb-3"
            message="Multiple orders or PDF rows matched the same data. Resolve manually via single-order transfer."
          />
          <Table
            rowKey={rowKeyForMatch}
            size="small"
            dataSource={ambiguousRows}
            columns={getReadyMatchColumns()}
            pagination={false}
          />
        </>
      ),
    });
  }

  const manualTransferredCount = reconcileData?.summary?.already_transferred_manual_count ?? 0;
  const bulkTransferredCount = reconcileData?.summary?.already_transferred_count ?? 0;
  const showDuplicateFingerprintWarning = hasDuplicateFingerprintOverlap(
    alreadyTransferredRows,
    unmatchedOrders
  );

  return (
    <>
      <Drawer
        title="Reconcile share transfers"
        open={open}
        onClose={handleClose}
        width={960}
        destroyOnClose
        extra={
          step === 'review' ? (
            <Space>
              <Button onClick={() => { setStep('upload'); setReconcileData(null); }}>
                Upload another PDF
              </Button>
              <Button
                type="primary"
                disabled={selectedReadyRows.length === 0}
                onClick={() => setConfirmOpen(true)}
              >
                Mark {selectedReadyRows.length} as transferred
              </Button>
            </Space>
          ) : null
        }
      >
        {step === 'upload' ? (
          <Spin spinning={extracting || matching} tip={extracting ? 'Extracting PDF…' : 'Matching orders…'}>
            <p className="text-sm text-gray-600 mb-4">
              Upload a share transfer PDF. We will extract ISIN, demat, qty, and date,
              then match against orders with status Payment Received.
            </p>
            <Dragger
              accept="application/pdf,.pdf"
              multiple={false}
              showUploadList={false}
              beforeUpload={handleUpload}
              disabled={extracting || matching}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag share transfer PDF here</p>
              <p className="ant-upload-hint">PDF only. OCR may take several minutes.</p>
            </Dragger>
          </Spin>
        ) : (
          <div className="space-y-4">
            {reconcileData?.summary ? (
              <Alert
                type="info"
                showIcon
                message={`${reconcileData.summary.extraction_count} PDF row(s) · ${reconcileData.summary.matched_ready} ready · ${reconcileData.summary.matched_blocked} blocked · ${manualTransferredCount} already transferred manually · ${bulkTransferredCount} already transferred via reconcile · ${reconcileData.summary.unmatched_extraction_count} unmatched PDF · ${reconcileData.summary.unmatched_order_count} unmatched orders`}
              />
            ) : null}
            {showDuplicateFingerprintWarning ? (
              <Alert
                type="warning"
                showIcon
                message="Same ISIN, demat, and qty appear in both Already transferred and Unmatched orders. Check PDF date and payment dates — a newer pending order may need manual review."
              />
            ) : null}
            <Tabs items={tabItems} />
            {bulkResult?.results?.some((r) => !r.success) ? (
              <Alert
                type="error"
                showIcon
                message="Some transfers failed"
                description={
                  <ul className="list-disc pl-4 mt-2 text-sm">
                    {bulkResult.results
                      .filter((r) => !r.success)
                      .map((r) => (
                        <li key={r.order_uuid}>
                          {r.order_uuid}: {r.message}
                        </li>
                      ))}
                  </ul>
                }
              />
            ) : null}
          </div>
        )}
      </Drawer>

      <Modal
        title="Confirm bulk share transfer"
        open={confirmOpen && open}
        onCancel={() => setConfirmOpen(false)}
        onOk={runBulkTransfer}
        confirmLoading={confirming}
        okText={`Transfer ${selectedReadyRows.length} order(s)`}
        zIndex={1200}
      >
        <p className="text-sm text-gray-600">
          This will mark {selectedReadyRows.length} matched order(s) as shares transferred
          using each row&apos;s PDF transfer date. Real inventory will be deducted per existing rules.
        </p>
      </Modal>
    </>
  );
}

export default ShareTransferReconcileDrawer;
